import { createSelector } from 'reselect';
import type { RootState } from '../index';
import type {
  ListDaysWithSummariesOutput,
  DaySummary,
} from '../../lib/api/content-api-client';

/**
 * Days slice selectors (task 5.3, public-website spec).
 *
 * Pure projections of `state.days` per design §3.1 (Selectors layer).
 * The server already returns days reverse-chronologically per design
 * §3.3, so these selectors preserve that ordering verbatim — the SPA
 * never re-sorts.
 *
 * Function-length budget: every export below stays under 20 lines per
 * Requirement 3.7.
 */

/**
 * Get a specific page of day summaries from the cache.
 *
 * Memoized — same `(state, page)` pair returns the same payload object
 * across renders so React's `Object.is` comparison short-circuits.
 *
 * The server already returns days reverse-chronologically; this
 * selector preserves that ordering by returning the payload's
 * `items` array verbatim.
 */
export const selectDaysPage = createSelector(
  [
    (state: RootState) => state.days.byPage,
    (_state: RootState, page: number) => page,
  ],
  (byPage, page): ListDaysWithSummariesOutput | undefined => byPage[page],
);

/**
 * Get the items array from a specific page in reverse-chronological
 * order (server-side order — preserved by the projection).
 */
export const selectDayItemsForPage = createSelector(
  [selectDaysPage],
  (page): ReadonlyArray<DaySummary> => page?.items ?? [],
);

/**
 * Get the per-page fetch status.
 */
export function selectDaysStatusByPage(
  state: RootState,
  page: number,
): 'idle' | 'loading' | 'succeeded' | 'failed' {
  return state.days.statusByPage[page] ?? 'idle';
}

/**
 * Get the per-page error message, if any.
 */
export function selectDaysErrorByPage(
  state: RootState,
  page: number,
): string | undefined {
  return state.days.errorByPage[page];
}
